import { SelectListItemRenderer } from '@acies/core';
import type DropdownItem from './dropdown-item';
import Icon from './icon';
import Label from './label';
import theme from './theme';

/** The DropdownItem renderer used in the List of the Dropdown component. */
export default class DropdownItemRenderer extends SelectListItemRenderer<DropdownItem> {
	#iconChild: Icon;
	#labelChild: Label;

	get #isPressed(): boolean {
		return this.styledState === 'pressed' || this.styledState === 'focusedPressed';
	}

	public constructor() {
		super();
		this.alignment = 'left';
		this.cornerRadius = 4;
		this.cursor = 'pointer';
		this.gapHorizontal = 10;
		this.height = 32;
		this.paddingHorizontal = 10;
		this.width = 'fill';
		this.#iconChild = new Icon();
		this.#iconChild.visible = false;
		this.#labelChild = new Label();
		this.#labelChild.visible = false;
	}

	protected override initialize(): void {
		super.initialize();
		this.addComponents([this.#iconChild, this.#labelChild]);
	}

	protected override itemChanged(): void {
		super.itemChanged();
		this.#updateIcon();
		this.#updateLabel();
	}

	protected override selectedChanged(): void {
		super.selectedChanged();
		this.#updateIdleSelectedStyles();
	}

	/** Asks the styled state rather than the state, so hovering has no look of its own: a row under the pointer looks as it would without it, and a selected one still looks selected. */
	protected override stateChanged(): void {
		super.stateChanged();
		if (this.styledState === 'idle') {
			this.#setIdleStyles();
		} else if (this.styledState === 'focused') {
			this.#setFocusedStyles();
		} else if (this.#isPressed) {
			this.#setPressedStyles();
		}
	}

	#setFocusedStyles(): void {
		this.fill = theme.colors.fill.focus;
		this.stroke = theme.strokes.focus;
	}

	#setIdleStyles(): void {
		this.#updateIdleSelectedStyles();
	}

	#setPressedStyles(): void {
		this.fill = null;
		this.stroke = null;
	}

	/** The icon child is only shown when the item has an icon. */
	#updateIcon(): void {
		if (this.item && this.item.icon) {
			this.#iconChild.name = this.item.icon;
		} else {
			this.#iconChild.name = null;
		}
		this.#iconChild.visible = this.#iconChild.name !== null;
	}

	#updateIdleSelectedStyles(): void {
		if (this.selected) {
			this.fill = theme.colors.fill.weak;
			this.stroke = null;
		} else {
			this.fill = null;
			this.stroke = null;
		}
	}

	/** The label child is only shown when the item has a text. */
	#updateLabel(): void {
		if (this.item && this.item.text) {
			this.#labelChild.text = this.item.text;
		} else {
			this.#labelChild.text = '';
		}
		this.#labelChild.visible = this.#labelChild.text !== '';
	}
}
customElements.define('ac-dropdown-item-renderer', DropdownItemRenderer);
